import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { getAdminProjectInformationTypes } from 'apis/adminProjectInformationTypes';
import { getAdminTypicalProjects } from 'apis/adminTypicalProjects';
import {
    deleteTypicalProjectInformationItem,
    getAdminTypicalProjectInformationItems,
} from 'apis/adminTypicalProjectInformationItems';
import { normalizeUploadedImageUrl } from 'apis/uploadImage';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const getTypeName = (type) => type?.name || type?.label || type?.translations?.[0]?.name || type?.code || '';
const getProjectTitle = (project) => project?.title || project?.translations?.[0]?.title || `Dự án ${project?.id}`;

const TypicalProjectInformationItemsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [typicalProjectIdFilter, setTypicalProjectIdFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
        typicalProjectId: typicalProjectIdFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter, typicalProjectIdFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery(
        'typical-project-information-items',
        queryParams,
        getAdminTypicalProjectInformationItems,
    );
    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: projectData = { items: [] } } = useAdminListQuery(
        'typical-projects-options',
        { pageSize: 1000, languageCode: languageCodeFilter || 'vi' },
        getAdminTypicalProjects,
    );
    const { data: typeData = { items: [] } } = useAdminListQuery(
        'project-information-types-options',
        { pageSize: 1000, languageCode: languageCodeFilter || 'vi', isActive: true },
        getAdminProjectInformationTypes,
    );
    const projects = projectData.items;
    const types = typeData.items;
    const deleteMutation = useAdminDeleteMutation('typical-project-information-items', deleteTypicalProjectInformationItem);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter, typicalProjectIdFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const getProjectLabel = (projectId) => {
        const project = projects.find((item) => Number(item.id) === Number(projectId));
        return project ? getProjectTitle(project) : `Dự án ${projectId}`;
    };

    const getType = (typeId) => types.find((item) => Number(item.id) === Number(typeId));

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) toast.success('Khóa thông tin dự án thành công');
            else toast.error(res.message || 'Khóa thông tin dự án thất bại');
        } catch {
            toast.error('Khóa thông tin dự án thất bại');
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        { header: 'Dự án', render: (row) => row.typicalProjectTitle || row.projectTitle || getProjectLabel(row.typicalProjectId) },
        {
            header: 'Loại',
            render: (row) => {
                const type = getType(row.projectInformationTypeId);
                const iconUrl = normalizeUploadedImageUrl(row.iconURL || row.iconUrl || type?.iconURL || type?.iconUrl || '');
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {iconUrl && <img src={iconUrl} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />}
                        <span>{row.label || getTypeName(type) || '—'}</span>
                    </div>
                );
            },
        },
        { header: 'Giá trị', render: (row) => row.value || row.translations?.[0]?.value || '—' },
        { field: 'sortOrder', header: 'Thứ tự' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/typical-project-information-items/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Khóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Thông tin dự án"
                subtitle="Gắn các thông tin phụ như địa chỉ, quy mô, diện tích cho từng dự án tiêu biểu"
                action={<Button onClick={() => navigate('/admin/typical-project-information-items/create')}>+ Thêm Thông Tin</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item">
                    <Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} />
                </div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={e => setLanguageCodeFilter(e.target.value)} options={languages.map(l => ({ value: l.code, label: l.name }))} />
                </div>
                <div className="filter-item">
                    <Select value={typicalProjectIdFilter} onChange={e => setTypicalProjectIdFilter(e.target.value)} options={[{ value: '', label: 'Tất cả dự án' }, ...projects.map(project => ({ value: project.id, label: getProjectTitle(project) }))]} />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Khóa thông tin dự án" message="Bạn có chắc chắn muốn khóa thông tin này không?" />
        </div>
    );
};

export default TypicalProjectInformationItemsList;
